<template>

  <div class="page hide-overflow">

    <!--Header-->
    <div class="page-header flex-shrink-0 border-bottom bg-white">
      <div class="container-fluid px-md-4 px-3">
        <div class="row no-gutters d-flex justify-content-between">
          <h3 class="mb-0"><i class="fas fa-calendar-check"></i>&nbsp;Events <b-spinner class="ml-2" v-show="isBusy"
              small label="Small Spinner"></b-spinner></h3>
          <b-form inline>
            <div>
              <router-link to="/EventAdd" class="btn btn-success" v-if="userPermissions.reportEvents"><i
                  class="fas fa-plus"></i> Add <span class="d-md-inline d-none">Event</span></router-link>
            </div>
          </b-form>
        </div>
      </div>
    </div>

    <!--Search-->
    <div class="container-fluid px-md-4 px-3 pt-1 pb-3 mb-3 bg-white border-bottom">
      <div v-if="true" class="row no-gutters">
        <div class="col-lg-4 col-md-6 col-12 d-flex flex-column justify-content-end">
          <div class="d-flex justify-content-between align-items-center">
            <label class="control-label">Search</label>
          </div>
          <b-form-input v-model="searchText" type="search" id="filterInput" placeholder="Type to Search"></b-form-input>
        </div>
        <div class="col-xl-3 col-lg-4 col-md-6 col-12 pl-md-3 pr-md-0 px-0 mt-md-0 mt-2">
          <div class="row">
            <div class="col">
              <label class="control-label">Event Status</label>
              <b-form-select v-model="statusType" :options="eventStatuses" @change="filterByEventStatus">
              </b-form-select>
            </div>
            <div class="col pl-0" v-if="statusType === 8 || statusType === 4">
              <label class="control-label">Claim Type</label>
              <b-form-select class="w-100" v-model="claimType"
                :options="[{ text: 'All', value: null }, { text: 'Property Liability', value: 0 }, { text: 'Resident Liability', value: 1 }, { text: 'Property Casualty', value: 2 }, { text: 'Vendor Liability', value: 3 }]"
                @change="filterByClaimType">
              </b-form-select>
            </div>
          </div>
        </div>
        <div class="col-auto">
          <div class="d-flex flex-column justify-content-end h-100">
            <b-button variant="link" :disabled="!canClearSearch" @click="resetFilters">Clear</b-button>
          </div>
        </div>
      </div>
    </div>

    <!--Table-->
    <div class="container-fluid flex-grow-1 overflow-auto px-md-4 px-3">
      <b-table id="events-table" class="mb-5 rg-table text-condensed" :items="eventProvider" :fields="fields" no-local-filtering
        :filter="filter" no-local-sorting :sort-desc.sync="sortDesc" :sort-by.sync="sortBy" :busy.sync="isBusy">
        <template v-slot:cell(property)="data">
          <span class="emphasis">{{ data.item.property }}</span>
        </template>
        <template v-slot:cell(severity)="data">
          <span :class="getClassName('severity', data.item.severity)" v-b-tooltip non-interactive no-fade :title="data.item.severity" class="emphasis"></span>
        </template>
        <template v-slot:cell(type)="data">
          <span :class="getClassName('type', data.item.type)" class="emphasis">{{ data.item.type }}</span>
        </template>
        <template v-slot:cell(category)="data">
          <span class="emphasis">{{ data.item.category }}</span>
        </template>
        <template v-slot:cell(location)="data">
          <span class="emphasis">{{ data.item.location }}</span>
        </template>
        <template v-slot:cell(status)="data">
          <b-tooltip v-if="!data.item.isSopComplete" :target="data.item.id" non-interactive no-fade>SOP Questions are
            incomplete</b-tooltip>
          <span class="d-flex flex-column position-relative">
            <span :id="data.item.id" :class="getClassName('status', data.item.status)">{{data.item.status}}</span>
            <span class="p-0 position-absolute"
              :class="data.item.isSopComplete ? 'claim-sop-complete' : 'claim-sop-incomplete'"></span>
          </span>
        </template>
        <template v-slot:cell(claimTypeString)="data">
          <span :class="getClassName('claim-type', data.item.claimTypeString)" class="emphasis">{{data.item.claimTypeString}}</span>
        </template>
        <template v-slot:cell(visibleTo)="data">
          <div class="text-center">
            <!--Need to adjust, fa fa-lock text-danger = Not All, fa fa-unlock text-success = All-->
            <!--{{formatPositionTypes(data.item.visibleToPositionTypes)}}-->
            <i v-if="data.item.visibleToPositionTypes.length == 0" class="fa fa-unlock text-success"></i>
            <i v-if="data.item.visibleToPositionTypes.length > 0" class="fa fa-lock text-danger"
              v-b-tooltip
              non-interactive
              no-fade
              :title="formatPositionTypes(data.item.visibleToPositionTypes)"
              ></i>
          </div>
        </template>
        <template v-slot:cell(dateOccurredLocal)="data">
          <span class="emphasis">{{ data.item.dateOccurredLocal | formatDate }}</span>
        </template>
        <template v-slot:cell(number)="data">
          <span class="emphasis">{{ data.item.number }}</span>
        </template>
        <template v-slot:cell(lastActivity)="data">
          <span v-if="isValidDate(data.item.lastActivity)" class="text-center emphasis">{{ data.item.lastActivity | formatDateNoTime }}</span>
          <span v-else class="text-center text-danger font-weight-bold emphasis">New!</span>
        </template>
        <template v-slot:cell(id)="data">
          <div class="d-flex flex-nowrap justify-content-end">
            <b-button
              v-if="!data.item.isSopComplete"
              title="Resume SOP"
              variant="light"
              class="mr-1 d-flex align-items-center text-primary border"
              @click="doResumeSop(data.item.id)">
              <i class="fas fa-play-circle"></i>
            </b-button>
            <router-link title="Edit Event" :to="{ name: 'EventManage', params: { id: data.item.id }}"
              class="btn btn-primary border-0 align-items-center d-flex">
                <small class="d-md-block d-none">Manage</small>
                <span class="d-md-none d-block text-primary"><i class="fa fa-pencil"></i></span>
            </router-link>
          </div>
        </template>
      </b-table>
    </div>
  </div>

</template>
<script>
import { mapActions } from 'vuex'
import moment from 'moment'

// component code
export default {
  name: 'Events',
  components: {
  },
  data () {
    return {
      fields: [],
      filter: null,
      searchText: null,
      events: [],
      isBusy: false,
      sortDesc: true,
      sortBy: 'dateOccurredLocal',
      statusType: -2,
      claimType: null,
      batchChangesInProgress: false,
      eventStatistics: [],
      eventStatuses:
      [
        {
          text: 'All - Excluding Complete',
          value: -2
        },
        {
          text: 'All - Including Complete',
          value: -1
        },
        {
          text: 'New',
          value: 0
        },
        {
          text: 'In Progress',
          value: 1
        },
        {
          text: 'Pending Approval',
          value: 2
        },
        {
          text: 'Approved',
          value: 3
        },
        {
          text: 'SOP In Progress',
          value: 5
        },
        {
          text: 'Claim in Progress',
          value: 8
        },
        {
          text: 'Complete',
          value: 4
        }
      ]
    }
  },
  watch: {
    searchText: {
      handler (newValue, oldValue) {
        if (this.searchTimeout) {
          clearTimeout(this.searchTimeout)
        }
        // Set new timeout
        this.searchTimeout = setTimeout(() => {
          // Perform search
          this.filter = newValue
          localStorage.setItem('events-search-text', newValue)
        }, 500)
      }
    }
  },
  created () {
    this.fields = [ 'property', { key: 'property', sortable: true, width: 150 }, { key: 'severity', sortable: true }, { key: 'type', sortable: true }, { key: 'category', sortable: true }, { key: 'location', sortable: true }, { key: 'status', sortable: true }, { key: 'claimTypeString', label: 'Claim Type', sortable: true }, { key: 'visibleTo', label: 'Privacy', sortable: false }, { key: 'dateOccurredLocal', label: 'Date/Time', sortable: true }, 'number', { key: 'lastActivity', label: 'Last Activity', sortable: true, thClass: 'text-nowrap' }, { key: 'id', label: 'Actions' } ]

    // Update Event Status Filter from localStorage
    let fes = localStorage.getItem('filter-event-status')
    if (fes != null) {
      this.statusType = Number(fes)
    }

    // Update Claim Status Filter from localStorage
    let fct = localStorage.getItem('filter-claim-type')
    if (fct != null) {
      this.claimType = Number(fct)
    }

    // Update Search Text from localStorage
    let est = localStorage.getItem('events-search-text')
    this.searchText = est || ''
  },
  methods: {
    ...mapActions('events', ['getEvents', 'resumeEvent']),

    async doResumeSop (eventId) {
      await this.resumeEvent(eventId)
      this.$router.push({ path: '/eventAdd', query: { resumeSop: 'true' } })
    },

    eventProvider (ctx) {
      if (this.filter !== null) {
        this.isBusy = true
        console.log(
          'event table ctx is: ' +
            JSON.stringify(ctx, null, 2)
        )
        var request = { sortBy: ctx.sortBy ? ctx.sortBy : 'dateOccurredLocal', sortDirection: ctx.sortDesc ? 'desc' : 'asc', search: ctx.filter, statusType: this.statusType, claimType: this.claimType }

        // Must return a promise that resolves to an array of items
        return this.getEvents(request).then(r => {
          // Pluck the array of items off our axios response
          const items = r.data.events
          console.log(
            'Returned Table Data is: ' +
            JSON.stringify(items, null, 2)
          )
          this.eventStatistics = r.data.eventStatistics
          this.isBusy = false
          // Must return an array of items or an empty array if an error occurred
          return items || []
        })
      } else {
        return []
      }
    },
    formatPositionTypes (positionTypes) {
      if (typeof Array.isArray(positionTypes) && positionTypes.length) {
        return positionTypes.map(a => a.name.trim()).join(', ')
      } else {
        return 'All'
      }
    },
    filterByEventStatus (statusType) {
      if (statusType !== 8) {
        this.claimType = null
        localStorage.removeItem('filter-claim-type')
      }
      this.statusType = statusType
      localStorage.setItem('filter-event-status', statusType)
      this.refreshGrid()
    },
    filterByClaimType (claimType) {
      this.claimType = claimType
      localStorage.setItem('filter-claim-type', claimType)
      this.refreshGrid()
    },
    refreshGrid () {
      if (this.batchChangesInProgress === false) {
        this.$root.$emit('bv::refresh::table', 'events-table')
      }
    },
    resetFilters () {
      this.batchChangesInProgress = true
      localStorage.removeItem('filter-claim-type')
      localStorage.removeItem('filter-event-status')
      this.searchText = ''
      this.claimType = null
      this.statusType = -2
      this.batchChangesInProgress = false
      this.refreshGrid()
    },
    getClassName (label, text) {
      if (text === null || text.trim() === '') {
        return `${label}-none`
      }
      var result = `${label}-${this.toKebabCase(text)}`
      return result
    },
    toKebabCase (str) {
      return str
        .replace(/([a-z])([A-Z])/g, '$1-$2') // Handle camelCase
        .replace(/[\s/_]+/g, '-') // Replace spaces, slashes, and underscores with hyphens
        .toLowerCase()
    },
    isValidDate (date) {
      if (!date) return false
      const eventDate = moment(date)
      return eventDate.isValid()
    }
  },
  computed: {
    userPermissions () {
      return this.$store.state.loggedInUserPermissions
    },
    canClearSearch () {
      return this.claimType !== null || this.statusType !== null || (this.searchText != null && this.searchText.length > 0)
    }
  }
}
</script>

<style scoped>
    @media (max-width: 767px) {
        thead {
            display: none !important;
        }
    }
    .selected {
      background-color: lightblue;
    }
    .card-body {
      cursor: pointer;
      padding: .6em 1em .25em;
    }
    .card-body > h5 {
      color: rgb(89, 165, 89);
      font-size: 1.8em;
      font-weight: 800;
      margin:.1em;
    }
    .card {
      height:50px;
    }
    .statistics-container {
      background-color: #fff;
      border: 2px solid lightgray;
      border-radius: 10px;
      padding:.1em .7em 0;
    }
    .statistics-container > label {
      font-size: small;
      margin:.1em;
    }

    .statistics-container > h5 {
      color: rgb(89, 165, 89);
      font-size: 1.8em;
      font-weight: 800;
      margin:.1em;
    }
    .truncate {
      display: inline-block;
      white-space: nowrap;      /*keep text on one line */
      overflow: hidden;         /*prevent text from being shown outside the border */
      text-overflow: ellipsis;  /*cut off text with an ellipsis*/
      font-size: .9em;
    }

    /*Table*/
    .rg-table td span{
      border-radius: 0.5em;
      display: block;
      display: -webkit-box;
      line-clamp: 1;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
      line-height: 1.2;
      text-overflow: ellipsis;
    }

    .rg-table td span:before {
      margin-right: .4em;
      font-family: FontAwesome;
    }

    /*Severity*/
    .severity-low:before {
      content: "\f01a";
      color: #28a745;
    }
    .severity-high:before {
      content: "\f01b";
      color: #dc3545;
    }
    [class*="severity"] {
      width: 22px;
      font-size: 120%;
      overflow: hidden;
      display: block;
      margin: auto;
    }
    [class*="status"],
    [class*="claim"] {
      padding: 0 0.6em 0 0.6em !important;
    }

    .rg-table td span[class*="claim"],
    .rg-table td span[class*="status"] {
      border-radius: 0.5em;
      display: block;
      display: -webkit-box;
      line-clamp: 2;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      line-height: 1.2;
      height: 36px;
      display: flex;
      align-items: center;
    }

    /*Type*/
    .type-chemical-biohazard:before {
      content: "\f071";
      font-family: FontAwesome;
    }
    .type-crime:before {
      content: "\f132";
    }
    .type-disaster-recovery:before {
      content: "\f1cd";
    }
    .type-fire:before {
      content: "\f06d";
    }
    .type-inclement-weather:before {
      content: "\f0e7";
    }
    .type-injuries:before {
      content:"\f0fa";
    }
    .type-natural-disaster:before {
      content: "\f0c2";
    }
    .type-structural:before {
      content: "\f0f7";
    }
    .type-task:before {
      content: "\f0ca";
    }
    .type-water-flood:before {
      content: "\f043";
    }

    /*Status*/
    .status-all-excluding-complete {
      background-color: #fae1e3;
      color: #89151c;
    }
    .status-all-including-complete,
    .status-approved {
      background-color: #d4edda;
      color: #00582d;
    }
    .status-new {
      background-color: #f5f5f5;
      color: #187f2c;
    }
    .status-new:before {
      content: "\f02b";
    }
    .status-in-progress,
    .status-report-in-progress,
    .status-claim-in-progress {
      background-color: #e5f2ff;
      color: #00518d;
    }
    .status-pending-approval {
      background-color: #fff3cd;
      color: #7c5e25;
    }
    .status-approved:before {
      content: "\f024";
    }
    .status-complete {
      background-color: #73c686;
      color: #ffffff;
    }
    .status-complete:before {
      content: "\f058";
    }

    /*Claim Type*/
    .claim-type-property-liability {
      background-color: #e4e4f2;
      color: #473674;
    }
    .claim-type-resident-liability {
      background-color: #f5d9e9;
      color: #a04346;
    }
    .claim-type-property-casualty {
      background-color: #fee0c2;
      color: #8c5429;
    }
    .claim-type-vendor-liability {
      background-color: #e4eb9c;
      color: #5f7835;
    }
    .claim-type-vendor-none {
      background-color: #f5f5f5;
      color: #666666;
    }
    .claim-sop-complete,
    .claim-sop-incomplete {
      width: 17px;
      height: 17px;
      right: -2px;
      top: 1px;
      padding: 0 !important;
      font-size: 90%;
      height: 100%;
      display: flex !important;
      align-items: center;
    }
    .claim-sop-complete:before {
      content: "\f058";
      color: #28a745;
    }
    .claim-sop-incomplete:before {
      content: "\f06a";
      color: #dc3545;
    }
</style>

<style>
  #events-table {
    table-layout: fixed;
  }
  #events-table thead tr th {
    vertical-align: middle;
  }
  #events-table thead tr th:nth-child(4) {
    width: 100px;
  }
  #events-table thead tr th:nth-child(2),
  #events-table thead tr th:nth-child(8){
    width: 90px;
    text-align: center;
  }
  #events-table thead tr th:last-child {
    text-align: center;
  }
  #events-table tbody tr td:first-child {
    background-color: #fafafa;
  }
  #events-table tbody tr td {
    border-right: none;
    border-left: none;
    border-bottom: 3px solid #f1f1f1;
    padding: 0.5em 0.75em;
  }
  @media screen and (max-width: 767px) {
    #events-table tbody tr td {
      border-right: none;
      border-left: none;
      border-bottom: none;
    }
    #events-table tbody tr td span,
    #events-table tbody tr td div {
      text-align: left !important;
      margin: 0;
    }
  }
</style>



// WEBPACK FOOTER //
// src/views/Events.vue